import type { Value } from '@platejs/slate';

import { MEETING_SLOT_OPEN_RE, SLOT_ID_RE } from '@/lib/email/meetingSlotTag';

import { SIGNATURE_BLOCK_MARKER_ATTR } from './constants';
import { sanitizeUrl } from './plateEmailSerializer';

// Returns a Plate image node for the given HTML image attributes, or null if
// there is nothing to render (about:blank src with no data-file-id).
function buildImagePlateNode(
  src: string,
  alt: string,
  style: string,
  dataFileId: string | null
): Record<string, unknown> | null {
  if (!dataFileId && (!src || src === 'about:blank')) {
    return null;
  }
  let width: 'small' | 'medium' | 'large' | number = 'medium';
  const widthMatch = style.match(/width:\s*(\d+)px/);
  if (widthMatch) {
    const widthPx = parseInt(widthMatch[1], 10);
    if (widthPx <= 200) {
      width = 'small';
    } else if (widthPx <= 400) {
      width = 'medium';
    } else {
      width = widthPx;
    }
  } else if (style.includes('width: 100%')) {
    width = 'large';
  }
  if (dataFileId) {
    const parsedFileId = parseInt(dataFileId, 10);
    if (!Number.isNaN(parsedFileId)) {
      return {
        type: 'img',
        url: '',
        alt,
        fileId: parsedFileId,
        width,
        pendingUrlResolution: true,
        children: [{ text: '' }],
      };
    }
  }
  const fileIdMatch = src.match(/^file:(\d+)$/);
  if (fileIdMatch) {
    return {
      type: 'img',
      url: '',
      alt,
      fileId: parseInt(fileIdMatch[1], 10),
      width,
      pendingUrlResolution: true,
      children: [{ text: '' }],
    };
  }
  return {
    type: 'img',
    url: src,
    alt,
    width,
    children: [{ text: '' }],
  };
}

/**
 * Guarantees an editable block above a leading signature.
 *
 * A signature is a VOID node — it holds no editable text — so when it is the
 * first block there is nowhere to place the caret and the user cannot type
 * above their own signature. That is exactly the state a brand-new email opens
 * in, where the whole body is still to be written.
 *
 * The empty paragraph cannot simply be seeded as `<p><br></p>` upstream: this
 * parser drops empty paragraphs deliberately (quoted replies are full of them,
 * and keeping them would pad every incoming email with blank lines), so a
 * seeded one never survives the parse. This restores the single empty
 * paragraph that carries meaning, at the one point where that is knowable.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches the untyped Plate node array built below
function withLeadingEditableBlock(nodes: any[]): any[] {
  return nodes[0]?.type === 'signature'
    ? [{ type: 'p', children: [{ text: '' }] }, ...nodes]
    : nodes;
}

/**
 * Converts an HTML string to a Plate Value array.
 * Handles headings, blockquotes, lists, paragraphs, divs, images (including
 * file:ID references), inline formatting (bold, italic, underline, links),
 * and falls back to plain text on parse errors.
 */
export function parseHtmlToPlateValue(html: string): Value {
  if (html && html.trim()) {
    try {
      // Replace file:ID URLs with data-file-id to prevent browser file:// errors
      let sanitizedValue = html.replace(
        /<img([^>]*)\ssrc=["']file:(\d+)["']([^>]*)>/gi,
        '<img$1 src="about:blank" data-file-id="$2"$3>'
      );
      // Normalize self-closing <entity .../> and <meeting-slot .../> to a
      // paired tag BEFORE DOMParser. DOMParser (text/html) does not honor the
      // self-closing `/` on an unknown tag like <entity> or <meeting-slot>, so
      // `<p>How about <meeting-slot id="X"/> or later?</p>` would swallow
      // ' or later?' as the tag's child text.
      // Anchor the tag name with the exact-boundary lookahead `(?=[\s/>])`
      // (the same boundary MEETING_SLOT_OPEN_RE uses), not `\b`. `\b` asserts
      // only a word boundary, which EXISTS between `y` and `-`, so `<entity\b`
      // also matches an unrelated custom element like `<entity-badge/>` and
      // would rewrite it into malformed `<entity-badge></entity>`. `(?=[\s/>])`
      // requires the char after `entity` to be whitespace, `/`, or `>`, so only
      // the exact `<entity>` element is normalized.
      sanitizedValue = sanitizedValue.replace(
        /<entity(?=[\s/>])([^>]*?)\/>/gi,
        '<entity$1></entity>'
      );
      sanitizedValue = sanitizedValue.replace(
        MEETING_SLOT_OPEN_RE,
        (match, attrs) =>
          match.endsWith('/>')
            ? `<meeting-slot${attrs.slice(0, -1)}></meeting-slot>`
            : match
      );
      // Parse via DOMParser rather than assigning innerHTML on a live-document
      // element. Even a detached div still belongs to the live document, so an
      // <img onerror=...> in the source HTML fires there; DOMParser's inert
      // document has no browsing context, so resources never load and inline
      // handlers never run.
      const tempDiv = new DOMParser().parseFromString(
        sanitizedValue,
        'text/html'
      ).body;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Plate node tree contains mixed types (text, links, blocks) without a union type export
      const nodes: any[] = [];

      const processInlineContent = (
        element: HTMLElement,
        marks: Record<string, boolean> = {}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Plate node tree contains mixed types (text, links, blocks) without a union type export
      ): Array<any> => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Plate node tree contains mixed types (text, links, blocks) without a union type export
        const children: Array<any> = [];

        element.childNodes.forEach(child => {
          if (child.nodeType === Node.TEXT_NODE) {
            const text = child.textContent || '';
            if (text) {
              children.push({ text, ...marks });
            }
          } else if (child.nodeType === Node.ELEMENT_NODE) {
            const childElement = child as HTMLElement;
            const tagName = childElement.tagName.toLowerCase();

            if (tagName === 'br') {
              children.push({ text: '\n' });
            } else if (tagName === 'a') {
              const href = sanitizeUrl(childElement.getAttribute('href') || '');
              const linkChildren = processInlineContent(childElement, marks);
              if (linkChildren.some(c => c.text)) {
                children.push({ type: 'a', url: href, children: linkChildren });
              }
            } else if (tagName === 'strong' || tagName === 'b') {
              children.push(
                ...processInlineContent(childElement, { ...marks, bold: true })
              );
            } else if (tagName === 'em' || tagName === 'i') {
              children.push(
                ...processInlineContent(childElement, {
                  ...marks,
                  italic: true,
                })
              );
            } else if (tagName === 'u') {
              children.push(
                ...processInlineContent(childElement, {
                  ...marks,
                  underline: true,
                })
              );
            } else if (tagName === 'meeting-slot') {
              const rawId = childElement.getAttribute('id');
              if (rawId && SLOT_ID_RE.test(rawId)) {
                // <meeting-slot id="UUID"/> is a slot chip reference.
                // Canonicalize to lowercase: an uppercase UUID resolves fine
                // at the API but never matches the resolver's canonical
                // lowercase response, leaving the chip unlabelled forever.
                children.push({
                  type: 'slot',
                  slotId: rawId.toLowerCase(),
                  children: [{ text: '' }],
                });
                // A valid paired `<meeting-slot id="X">fallback</meeting-slot>`
                // can carry author text folded in by the HTML parser. Re-emit
                // that inner content as a sibling after the chip so it is not
                // lost — mirrors the backend send path (substitute_slot_ref
                // appends captured inner text after the rendered time). The
                // empty self-closing form yields only a [{text:''}] placeholder,
                // which is filtered out so it adds no spurious node.
                const slotFallback = processInlineContent(
                  childElement,
                  marks
                ).filter(node => node.type || node.text !== '');
                if (slotFallback.length > 0) {
                  children.push(...slotFallback);
                }
              } else {
                // A missing or non-UUID id means the tag can never be
                // resolved. Render a visible failed chip instead of silently
                // dropping it (issue #12526 AC2) — a meeting-slot tag must
                // never contribute nothing. `resolutionAttempted: true`
                // prevents PlateEmailEditor's resolution effect from calling
                // the resolve endpoint with a bad id; `resolutionFailed: true`
                // with no `label` makes SlotElement render "Unable to load
                // time".
                children.push({
                  type: 'slot',
                  slotId: (rawId ?? '').toLowerCase(),
                  resolutionAttempted: true,
                  resolutionFailed: true,
                  children: [{ text: '' }],
                });
              }
            } else {
              children.push(...processInlineContent(childElement, marks));
            }
          }
        });

        return children.length > 0 ? children : [{ text: '' }];
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Plate node tree contains mixed types
      const processBlockWithImages = (element: HTMLElement): any[] => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Plate node tree contains mixed types
        const result: any[] = [];
        let pendingInline: ChildNode[] = [];

        const flushInline = () => {
          if (pendingInline.length === 0) {
            return;
          }
          const wrapper = document.createElement('span');
          for (const n of pendingInline) {
            wrapper.appendChild(n.cloneNode(true));
          }
          const children = processInlineContent(wrapper);
          if (
            children.some(
              c => c.text?.trim() || c.type === 'a' || c.type === 'slot'
            )
          ) {
            result.push({ type: 'p', children });
          }
          pendingInline = [];
        };

        element.childNodes.forEach(child => {
          if (
            child.nodeType === Node.ELEMENT_NODE &&
            (child as HTMLElement).tagName.toLowerCase() === 'img'
          ) {
            flushInline();
            const imgEl = child as HTMLElement;
            const imgNode = buildImagePlateNode(
              imgEl.getAttribute('src') || '',
              imgEl.getAttribute('alt') || '',
              imgEl.getAttribute('style') || '',
              imgEl.getAttribute('data-file-id')
            );
            if (imgNode) {
              result.push(imgNode);
            }
          } else {
            pendingInline.push(child);
          }
        });
        flushInline();
        return result;
      };

      const parseListElement = (
        listEl: HTMLElement,
        listType: 'ul' | 'ol'
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Plate node tree contains mixed types
      ): any | null => {
        const items = Array.from(listEl.children)
          .filter(child => child.tagName.toLowerCase() === 'li')
          .map(li => {
            const liEl = li as HTMLElement;

            // Separate nested <ul>/<ol> children from inline children
            const inlineNodes: ChildNode[] = [];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Plate node tree contains mixed types
            const nestedLists: any[] = [];

            Array.from(liEl.childNodes).forEach(child => {
              if (child.nodeType === Node.ELEMENT_NODE) {
                const tag = (child as HTMLElement).tagName.toLowerCase();
                if (tag === 'ul') {
                  const nested = parseListElement(child as HTMLElement, 'ul');
                  if (nested) {
                    nestedLists.push(nested);
                  }
                  return;
                }
                if (tag === 'ol') {
                  const nested = parseListElement(child as HTMLElement, 'ol');
                  if (nested) {
                    nestedLists.push(nested);
                  }
                  return;
                }
              }
              inlineNodes.push(child);
            });

            if (nestedLists.length > 0) {
              // Wrap the inline portion in <lic> so it is a sibling of the
              // nested <ul>/<ol> inside the <li> (the structure that
              // @platejs/list-classic expects for nested-list items).
              const wrapper = document.createElement('span');
              for (const n of inlineNodes) {
                wrapper.appendChild(n.cloneNode(true));
              }
              const inlineChildren = processInlineContent(wrapper);
              return {
                type: 'li',
                children: [
                  { type: 'lic', children: inlineChildren },
                  ...nestedLists,
                ],
              };
            }

            // No nested list — preserve existing flat behavior.
            return {
              type: 'li',
              children: processInlineContent(liEl),
            };
          });

        return items.length > 0 ? { type: listType, children: items } : null;
      };

      Array.from(tempDiv.childNodes).forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent?.trim();
          if (text) {
            nodes.push({ type: 'p', children: [{ text }] });
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as HTMLElement;

          // A signature block is stored as an opaque void node: never descend into it,
          // so tables, nested divs, inline styles, and logos survive verbatim. The
          // marker is added only by the account signature editor, so compose content
          // and unmarked tables are unaffected.
          if (element.getAttribute(SIGNATURE_BLOCK_MARKER_ATTR) !== null) {
            nodes.push({
              type: 'signature',
              html: element.outerHTML,
              children: [{ text: '' }],
            });
            return; // skip block handling for this node; continue the forEach
          }

          switch (element.tagName.toLowerCase()) {
            case 'h1':
              nodes.push({
                type: 'h1',
                children: processInlineContent(element),
              });
              break;
            case 'h2':
              nodes.push({
                type: 'h2',
                children: processInlineContent(element),
              });
              break;
            case 'h3':
              nodes.push({
                type: 'h3',
                children: processInlineContent(element),
              });
              break;
            case 'blockquote':
              nodes.push({
                type: 'blockquote',
                children: processInlineContent(element),
              });
              break;
            case 'ul': {
              const listNode = parseListElement(element, 'ul');
              if (listNode) {
                nodes.push(listNode);
              }
              break;
            }
            case 'ol': {
              const listNode = parseListElement(element, 'ol');
              if (listNode) {
                nodes.push(listNode);
              }
              break;
            }
            case 'p':
            case 'div': {
              if (element.querySelector('img')) {
                nodes.push(...processBlockWithImages(element));
              } else {
                const children = processInlineContent(element);
                if (
                  children.some(
                    child =>
                      child.text?.trim() ||
                      child.type === 'a' ||
                      child.type === 'slot'
                  )
                ) {
                  nodes.push({ type: 'p', children });
                }
              }
              break;
            }
            case 'img': {
              const imgNode = buildImagePlateNode(
                element.getAttribute('src') || '',
                element.getAttribute('alt') || '',
                element.getAttribute('style') || '',
                element.getAttribute('data-file-id')
              );
              if (imgNode) {
                nodes.push(imgNode);
              }
              break;
            }
            default: {
              const text = element.textContent?.trim();
              if (text) {
                nodes.push({ type: 'p', children: [{ text }] });
              }
              break;
            }
          }
        }
      });

      return nodes.length > 0
        ? withLeadingEditableBlock(nodes)
        : [{ type: 'p', children: [{ text: '' }] }];
    } catch (error) {
      console.error('Error parsing HTML:', error);
      // Use DOMParser (inert document) rather than a regex strip so the text
      // extraction is handled by the browser's HTML parser — the same
      // mechanism used in the happy path above. No regex pattern to flag.
      const textValue =
        new DOMParser().parseFromString(html, 'text/html').body.textContent ??
        '';
      return [{ type: 'p', children: [{ text: textValue }] }];
    }
  }
  return [{ type: 'p', children: [{ text: '' }] }];
}
